import React from "react";
import styled from "@emotion/styled";
import { Subscribe } from "unstated";
import { RouteComponentProps } from "react-router";
import AuthContainer from "../../state/containers/AuthContainer";
import UserContainer from "../../state/containers/UserContainer";
import OrganizationContainer from "../../state/containers/OrganizationContainer";
import ApplicationContainer from "../../state/containers/ApplicationContainer";
import FeatureContainer from "../../state/containers/FeatureContainer";
import Splash from "../splash";
import LandingContainer from "../../components/structure/LandingContainer";
import Loader from "../../components/general/Loader";
import Button from "../../components/general/Button";
import InvitationPreviewModel from "../../models/organizations/api/InvitationPreviewModel";
import * as voicifyApi from "../../api";
import { getInvitationCreateAccountPath, getInvitationSignInPath } from "../login/invitationRoutes";

interface AcceptRouteProps {
    invitationCode?: string;
}

interface AcceptInvitationProps extends RouteComponentProps<AcceptRouteProps> { }

interface InvitationLandingProps extends AcceptInvitationProps {
    authContainer: AuthContainer;
    userContainer: UserContainer;
    orgContainer: OrganizationContainer;
}

type InvitationPhase = "loading" | "ready" | "accepting" | "success" | "mismatch" | "accessDenied" | "expired" | "inactive" | "error";

interface InvitationLandingState {
    phase: InvitationPhase;
    preview: InvitationPreviewModel;
    errors: string[];
}

export class InvitationLanding extends React.Component<InvitationLandingProps, InvitationLandingState> {
    private isAccepting = false;

    state: InvitationLandingState = {
        phase: "loading",
        preview: null,
        errors: []
    };

    componentDidMount() {
        const applicationId = new URLSearchParams(this.props.location.search).get("app");
        if (applicationId) {
            sessionStorage.setItem("invite_target_application_id", applicationId);
        }
        this.loadInvitation();
    }

    loadInvitation = async () => {
        const invitationCode = this.props.match.params.invitationCode;
        if (!invitationCode) {
            this.setState({ phase: "inactive", preview: null, errors: [] });
            return;
        }

        this.setState({ phase: "loading", preview: null, errors: [] });
        const result = await voicifyApi.getInvitationPreview(invitationCode);
        if (result.resultType === "NotFound") {
            this.setState({ phase: "inactive", preview: null, errors: [] });
            return;
        }
        if (result.resultType === "PermissionDenied" && this.props.authContainer.state.isAuthenticated) {
            this.setState({ phase: "accessDenied", preview: null, errors: result.errors || [] });
            return;
        }
        if (result.resultType !== "Ok") {
            this.setState({ phase: "error", preview: null, errors: result.errors || [] });
            return;
        }

        const preview = result.data;
        if (preview.isExpired) {
            this.setState({ phase: "expired", preview, errors: [] });
            return;
        }

        if (this.props.authContainer.state.isAuthenticated) {
            if (preview.isCurrentUserInvitee === true) {
                this.setState({ phase: "accepting", preview, errors: [] }, this.acceptInvitation);
            } else {
                this.setState({ phase: "mismatch", preview, errors: [] });
            }
            return;
        }

        this.setState({ phase: "ready", preview, errors: [] });
    };

    acceptInvitation = async () => {
        if (this.isAccepting) {
            return;
        }
        this.isAccepting = true;
        this.setState({ phase: "accepting", errors: [] });

        const result = await this.props.userContainer.acceptInvitation(this.props.match.params.invitationCode);
        this.isAccepting = false;

        if (result.resultType === "Ok") {
            const organizationsResult = await this.props.orgContainer.getOrganizations();
            if (organizationsResult.resultType === "Ok") {
                await this.props.orgContainer.setCurrentOrgById(result.data.organizationId);
            }

            this.setState({ phase: "success", errors: [] });
            const queryApplicationId = new URLSearchParams(this.props.location.search).get("app");
            const applicationId = sessionStorage.getItem("invite_target_application_id") || queryApplicationId;
            sessionStorage.removeItem("invite_target_application_id");
            window.setTimeout(() => {
                this.props.history.replace(applicationId ? `/v/apps/${applicationId}` : "/v");
            }, 600);
            return;
        }

        if (result.resultType === "PermissionDenied") {
            this.setState({ phase: "mismatch", errors: result.errors });
        } else if (result.resultType === "NotFound") {
            this.setState({ phase: "inactive", errors: result.errors });
        } else if (result.resultType === "Invalid") {
            this.setState({ phase: "expired", errors: result.errors });
        } else {
            this.setState({ phase: "error", errors: result.errors });
        }
    };

    createAccount = () => {
        const invitationCode = this.props.match.params.invitationCode;
        const search = this.props.location.search || "";
        this.props.history.push(getInvitationCreateAccountPath(invitationCode, search));
    };

    signIn = () => {
        const invitationCode = this.props.match.params.invitationCode;
        const search = this.props.location.search || "";
        this.props.history.push(getInvitationSignInPath(invitationCode, search));
    };

    switchAccount = () => {
        this.props.authContainer.signOut();
        const search = this.props.location.search || "";
        this.props.history.replace(`/login/${this.props.match.params.invitationCode}${search}`);
    };

    continueToSpark = () => {
        this.props.history.replace(this.props.authContainer.state.isAuthenticated ? "/v" : "/login");
    };

    render() {
        const { phase, preview, errors } = this.state;
        const organizationName = preview && preview.organizationName ? preview.organizationName : "this organization";

        return (
            <LandingContainer>
                <PageWrapper>
                    <InvitationCard>
                        {(phase === "loading" || phase === "accepting") && <Loader />}
                        {phase === "loading" && <><Title>Checking your invitation</Title><Body>We’re making sure this link is still active.</Body></>}
                        {phase === "accepting" && <><Title>Joining {organizationName}</Title><Body>Your account matches the invitation. We’re adding you now.</Body></>}
                        {phase === "success" && <><StatusMark>✓</StatusMark><Title>You’re in!</Title><Body>You joined {organizationName}. Taking you to Spark…</Body></>}
                        {phase === "ready" && <>
                            <Eyebrow>You’ve been invited</Eyebrow>
                            <Title>Join {organizationName}</Title>
                            <Body>This invitation was sent to <strong>{preview.maskedEmail}</strong>.</Body>
                            <Button themes={["primary", "wide"]} text="Create account" onClick={this.createAccount} />
                            <Button themes={["secondary", "wide"]} text="Already have an account? Sign in" onClick={this.signIn} />
                            <HelpText>Use the email address that received this invitation.</HelpText>
                        </>}
                        {phase === "mismatch" && <>
                            <Eyebrow>Different account needed</Eyebrow>
                            <Title>Switch accounts to join {organizationName}</Title>
                            <Body>This invitation was sent to <strong>{preview && preview.maskedEmail}</strong>, but you’re signed in as <strong>{preview && preview.currentUserEmail ? preview.currentUserEmail : "another account"}</strong>.</Body>
                            {errors.length > 0 && <ErrorText>{errors[0]}</ErrorText>}
                            <Button themes={["primary", "wide"]} text="Switch account" onClick={this.switchAccount} />
                        </>}
                        {phase === "accessDenied" && <>
                            <Eyebrow>Different account may be needed</Eyebrow>
                            <Title>Sign in with the invited account</Title>
                            <Body>We couldnâ€™t verify this invitation with your current account.</Body>
                            {errors.length > 0 && <ErrorText>{errors[0]}</ErrorText>}
                            <Button themes={["primary", "wide"]} text="Switch account" onClick={this.switchAccount} />
                        </>}
                        {phase === "expired" && <>
                            <Eyebrow>Invitation expired</Eyebrow>
                            <Title>Ask for a new invitation</Title>
                            <Body>This invitation is no longer active. Ask an organization administrator to resend it.</Body>
                            <Button themes={["secondary", "wide"]} text="Continue to Spark" onClick={this.continueToSpark} />
                        </>}
                        {phase === "inactive" && <>
                            <Eyebrow>Invitation unavailable</Eyebrow>
                            <Title>This link is no longer active</Title>
                            <Body>It may have already been accepted, revoked, or replaced by a newer invitation. If you still need access, ask an organization administrator to resend it.</Body>
                            <Button themes={["secondary", "wide"]} text="Continue to Spark" onClick={this.continueToSpark} />
                        </>}
                        {phase === "error" && <>
                            <Eyebrow>We couldn’t check this invitation</Eyebrow>
                            <Title>Please try again</Title>
                            <Body>{errors.length > 0 ? errors[0] : "Something went wrong while checking the invitation."}</Body>
                            <Button themes={["primary", "wide"]} text="Try again" onClick={this.loadInvitation} />
                        </>}
                    </InvitationCard>
                </PageWrapper>
            </LandingContainer>
        );
    }
}

const AcceptInvitation = (props: AcceptInvitationProps) => (
    <Subscribe to={[AuthContainer, UserContainer, OrganizationContainer, ApplicationContainer, FeatureContainer]}>
        {(authContainer: AuthContainer, userContainer: UserContainer, orgContainer: OrganizationContainer, appContainer: ApplicationContainer, featureContainer: FeatureContainer) => {
            if (authContainer.state.isAuthenticated && (authContainer.state.expirationDate < new Date() || !authContainer.state.accessToken)) {
                return <Splash authContainer={authContainer} orgContainer={orgContainer} appContainer={appContainer} featureContainer={featureContainer} />;
            }
            return <InvitationLanding {...props} authContainer={authContainer} userContainer={userContainer} orgContainer={orgContainer} />;
        }}
    </Subscribe>
);

const PageWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    flex: 1;
    padding: 24px 24px 64px;
`;

const InvitationCard = styled.div`
    box-sizing: border-box;
    width: 100%;
    max-width: 520px;
    min-height: 320px;
    padding: 48px;
    border-radius: 12px;
    background: white;
    box-shadow: 0 12px 32px rgba(31, 49, 71, 0.14);
    text-align: center;
    color: #1f3147;

    button {
        margin-top: 24px;
    }

    @media (max-width: 600px) {
        padding: 32px 24px;
    }
`;

const Eyebrow = styled.div`
    margin-bottom: 12px;
    color: #1874d1;
    font-size: 13px;
    font-weight: bold;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const Title = styled.h1`
    margin: 8px 0 16px;
    font-size: 30px;
    line-height: 1.25;
`;

const Body = styled.p`
    margin: 0 auto;
    max-width: 420px;
    color: #52677e;
    font-size: 16px;
    line-height: 1.6;
`;

const HelpText = styled.p`
    margin: 18px 0 0;
    color: #74879a;
    font-size: 13px;
`;

const ErrorText = styled.p`
    margin: 16px 0 0;
    color: #b42318;
    font-size: 14px;
`;

const StatusMark = styled.div`
    margin: 0 auto 16px;
    width: 54px;
    height: 54px;
    border-radius: 50%;
    background: #e6f7ed;
    color: #178447;
    font-size: 34px;
    line-height: 54px;
`;

export default AcceptInvitation;
